<template>
  <v-card>
    <v-card-title>Novo Facilitador</v-card-title>

    <v-card-text>
      <v-text-field
        label="Nome do Facilitador"
        class="mb-2"
        v-model="facilitatorTitle"
        outlined
        dense
        :error-messages="errors.facilitatorTitle"
        clearable
      />

      <v-row>
        <v-col>
          <SearchDropdown
            v-model="nextMedicalRequestItem"
            :search-result-items="medicalRequestItems"
            :loading="isMedicalRequestItemsLoading"
            item-text="name"
            input-prompt-text="Digite o nome de um serviço..."
            not-found-text="Nenhum serviço encontrado."
            label="Associar Serviço"
            @search="triggerSearch"
            @change="addItemToSelection"
            :error-messages="errors.selectedMedicalRequestItems"
          />
        </v-col>

        <v-col md="6">
          <v-select
            v-model="selectedMedicalRequestType"
            outlined
            dense
            label="Tipo de Serviço"
            :items="medicalRequestTypes"
            item-text="displayName"
            item-value="value"
            background-color="white"
          />
        </v-col>
      </v-row>

      <v-simple-table>
        <template v-slot:default>
          <thead>
            <tr>
              <th>Serviço</th>
              <th>Tipo</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in selectedMedicalRequestItems" :key="item.id">
              <td>
                {{ item.name }}
              </td>
              <td>
                {{ formatMedicalRequestItemType(item) }}
              </td>
              <td class="actions-column">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      small
                      text
                      v-on="on"
                      @click="removeItemFromSelection(item.id)"
                    >
                      <v-icon small>eva-trash-outline</v-icon>
                    </v-btn>
                  </template>

                  <span>Remover da Lista</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <v-row v-if="noMedicalRequestItemsSelected" justify="center">
        <v-col cols="6">
          <v-alert color="orange" outlined text type="info">
            Selecione ao menos um serviço.
          </v-alert>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="blue darken-1" text @click="clearAndClose">
        Cancelar
      </v-btn>

      <v-btn :loading="isSaving" color="blue darken-1" text @click="submit">
        Salvar
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {
  MEDICAL_REQUEST_TYPES,
  findMedicalRequestItemTypeDisplayName,
} from "@/components/config/medical_request/item_facilitators/medical_request_types.js";
import GET_MEDICAL_REQUEST_ITEMS_QUERY from "@/components/config/medical_request/item_facilitators/getMedicalRequestItems.gql";
import SAVE_MEDICAL_REQUEST_ITEM_FACILITATOR from "@/graphql/medical_request/save_medical_request_item_facilitator.gql";

import SearchDropdown from "@/components/ui/form/search_dropdown.vue";

export default {
  name: "MedicalRequestItemFacilitatorForm",
  components: { SearchDropdown },
  props: {
    facilitator: {
      type: Object,
      required: false,
      default: () => null,
    },
  },
  data() {
    return {
      facilitatorId: null,
      facilitatorTitle: null,
      searchQuery: null,
      selectedMedicalRequestType: null,
      nextMedicalRequestItem: null,
      selectedMedicalRequestItems: [],
      errors: {},
      getMedicalRequestItemsQuery: GET_MEDICAL_REQUEST_ITEMS_QUERY,
      medicalRequestTypes: MEDICAL_REQUEST_TYPES,
      isSaving: false,
    };
  },
  apollo: {
    medicalRequestItems: {
      query: require("@/components/config/medical_request/item_facilitators/getMedicalRequestItems.gql"),
      variables() {
        return {
          searchQuery: this.searchQuery,
          medicalRequestType: this.selectedMedicalRequestType,
        };
      },
      result() {
        this.$apollo.queries.medicalRequestItems.skip = true; // skip until next search
      },
      skip: true, // skip until search is triggered
    },
  },
  created() {
    if (this.facilitator) {
      this.loadFacilitatorData(this.facilitator);
    }
  },
  computed: {
    noMedicalRequestItemsSelected() {
      return this.selectedMedicalRequestItems.length === 0;
    },
    isMedicalRequestItemsLoading() {
      return this.$apollo.queries.medicalRequestItems.loading;
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },
  },
  methods: {
    loadFacilitatorData(facilitator) {
      this.facilitatorId = facilitator.id;
      this.facilitatorTitle = facilitator.title;
      this.selectedMedicalRequestItems = [...facilitator.items];
    },
    formatMedicalRequestItemType(item) {
      return findMedicalRequestItemTypeDisplayName(item.medicalRequestType);
    },
    triggerSearch(search) {
      this.searchQuery = search;
      this.$apollo.queries.medicalRequestItems.skip = false;
    },
    addItemToSelection(nextItem) {
      if (!nextItem) return;

      this.$nextTick(() => {
        this.nextMedicalRequestItem = null;
      });

      if (this.isItemSelected(nextItem)) return;

      this.selectedMedicalRequestItems.push(nextItem);
    },
    removeItemFromSelection(itemId) {
      this.selectedMedicalRequestItems = this.selectedMedicalRequestItems.filter(
        (item) => item.id !== itemId
      );
    },
    isItemSelected(newItem) {
      return this.selectedMedicalRequestItems
        .map((item) => item.id)
        .includes(newItem.id);
    },
    clearAndClose() {
      this.facilitatorTitle = null;
      this.selectedMedicalRequestType = null;
      this.selectedMedicalRequestItems = [];

      this.$emit("close");
    },
    async submit() {
      this.validate();

      if (this.hasErrors) return;

      await this.saveFacilitator();
      this.notifySaveSuccess();

      this.clearAndClose();

      this.$emit("refresh");
    },
    validate() {
      this.errors = {};

      if (!this.facilitatorTitle) {
        this.errors.facilitatorTitle = "Este campo deve ser preenchido.";
      }

      if (this.noMedicalRequestItemsSelected) {
        this.errors.selectedMedicalRequestItems =
          "Selecione ao menos um serviço.";
      }
    },
    async saveFacilitator() {
      const medicalRequestItemIds = this.selectedMedicalRequestItems.map(
        (item) => item.id
      );

      this.isSaving = true;

      try {
        await this.apolloMutate({
          mutation: SAVE_MEDICAL_REQUEST_ITEM_FACILITATOR,
          variables: {
            id: this.facilitatorId,
            title: this.facilitatorTitle,
            medicalRequestItemIds,
          },
        });
      } finally {
        this.isSaving = false;
      }
    },
    notifySaveSuccess() {
      const message = this.facilitatorId
        ? "Facilitador atualizado com sucesso."
        : "Facilitador cadastrado com sucesso.";

      this.$store.dispatch("createNotification", {
        message,
        type: "success",
      });
    },
  },
};
</script>

<style scoped>
td.actions-column {
  width: 1%;
  white-space: nowrap;
}
</style>
